import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useExerciseStore } from '../stores/exercisesStore';
import { useNewSubjectStore } from '../stores/newSubjectStore';

const FILTER_KEYS = [
  'favorite',
  'unanswered',
  'rightAnswer',
  'wrongAnswer',
  'difficulty',
  'isEnem',
  'trialOptions',
  'questionOptions',
  'institutionType',
  'selectedYear',
  'subjects',
] as const;

// Aplica os filtros vindos da URL no store sincronamente. Precisa rodar antes do
// primeiro fetch do ExerciseList — por isso é exposto como função standalone,
// não como efeito (efeitos filhos disparam antes dos do pai em mount).
export function applyExerciseFiltersFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const hasAnyFilter = FILTER_KEYS.some((key) => params.has(key));
  if (!hasAnyFilter) return;

  const {
    setFavorite,
    setUnanswered,
    setRightAnswer,
    setWrongAnswer,
    setDifficulty,
    setIsEnem,
    setTrialOptions,
    setQuestionOptions,
    setInstitutionType,
    setSelectedYear,
  } = useExerciseStore.getState();
  const { setSelectedSubjects } = useNewSubjectStore.getState();

  setFavorite(params.get('favorite') === 'true');
  setUnanswered(params.get('unanswered') === 'true');
  setRightAnswer(params.get('rightAnswer') === 'true');
  setWrongAnswer(params.get('wrongAnswer') === 'true');
  setDifficulty(params.getAll('difficulty'));
  setIsEnem(params.getAll('isEnem'));
  setTrialOptions(params.get('trialOptions') || 'last');
  setQuestionOptions(params.getAll('questionOptions'));
  setInstitutionType(params.getAll('institutionType'));
  setSelectedYear(params.getAll('selectedYear'));
  setSelectedSubjects(params.getAll('subjects'));
}

export function useExerciseUrlFilters() {
  const [, setSearchParams] = useSearchParams();

  const writeStoresToUrl = useCallback(() => {
    const state = useExerciseStore.getState();
    const { selectedSubjects } = useNewSubjectStore.getState();

    const params = new URLSearchParams();
    if (state.favorite) params.set('favorite', 'true');
    if (state.unanswered) params.set('unanswered', 'true');
    if (state.rightAnswer) params.set('rightAnswer', 'true');
    if (state.wrongAnswer) params.set('wrongAnswer', 'true');
    state.difficulty.forEach((v) => params.append('difficulty', v));
    state.isEnem.forEach((v) => params.append('isEnem', v));
    if (state.trialOptions && state.trialOptions !== 'last') {
      params.set('trialOptions', state.trialOptions);
    }
    state.questionOptions.forEach((v) => params.append('questionOptions', v));
    state.institutionType.forEach((v) => params.append('institutionType', v));
    state.selectedYear.forEach((v) => params.append('selectedYear', v));
    selectedSubjects.forEach((v) => params.append('subjects', v));

    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  return { writeStoresToUrl };
}
